import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MeasurementSummary,
  MeasurementReading,
  MeasurementDefinition,
  TrendAnalysis,
} from '@easydone/shared';
import {
  getMeasurementSummary,
  getMeasurementHistory,
  recordMeasurement,
  calculateTrend,
} from '../services/measurementService';
import { getMeasurementDefinitions } from '../services/measurementDefinitionService';
import { MeasurementChart } from './MeasurementChart';
import { MeasurementRecorder } from './MeasurementRecorder';
import {
  ChartBarIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';

interface MeasurementsWidgetProps {
  entityType: 'floc' | 'asset';
  entityId: string;
  entityName: string;
}

export function MeasurementsWidget({
  entityType,
  entityId,
  entityName,
}: MeasurementsWidgetProps) {
  const { currentUser } = useAuth();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();

  const [summaries, setSummaries] = useState<MeasurementSummary[]>([]);
  const [selectedMeasurement, setSelectedMeasurement] = useState<string | null>(null);
  const [measurementHistory, setMeasurementHistory] = useState<MeasurementReading[]>([]);
  const [trend, setTrend] = useState<TrendAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRecorder, setShowRecorder] = useState(false);
  const [periodDays, setPeriodDays] = useState(30);
  const [readingsPeriodDays, setReadingsPeriodDays] = useState(30);
  const [measurementDefinitions, setMeasurementDefinitions] = useState<MeasurementDefinition[]>([]);

  useEffect(() => {
    loadSummaries();
  }, [entityId, entityType, currentOrg]);

  useEffect(() => {
    if (selectedMeasurement && currentOrg) {
      loadMeasurementDetails(selectedMeasurement);
    }
  }, [selectedMeasurement, periodDays, readingsPeriodDays, currentOrg]);

  const loadSummaries = async () => {
    if (!currentOrg) return;

    setLoading(true);
    try {
      const [data, definitions] = await Promise.all([
        getMeasurementSummary(currentOrg.id, entityType, entityId),
        getMeasurementDefinitions(currentOrg.id),
      ]);

      setSummaries(data);
      setMeasurementDefinitions(definitions);

      // Auto-select first measurement if none selected
      if (data.length > 0 && !selectedMeasurement) {
        setSelectedMeasurement(data[0].measurementDefId);
      }
    } catch (error) {
      console.error('Failed to load measurement summaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMeasurementDetails = async (measurementDefId: string) => {
    if (!currentOrg) return;

    try {
      // Calculate date range for readings
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - readingsPeriodDays);

      const [history, trendData] = await Promise.all([
        getMeasurementHistory(
          currentOrg.id,
          entityType,
          entityId,
          measurementDefId,
          {
            startDate,
            endDate,
            sortOrder: 'desc', // Most recent first
          }
        ),
        calculateTrend(currentOrg.id, entityType, entityId, measurementDefId, periodDays),
      ]);
      setMeasurementHistory(history);
      setTrend(trendData);
    } catch (error) {
      console.error('Failed to load measurement history:', error);
    }
  };

  const handleRecordMeasurements = async (measurements: any[]) => {
    if (!currentOrg || !currentUser) return;

    try {
      for (const measurement of measurements) {
        await recordMeasurement(currentOrg.id, {
          ...measurement,
          orgId: currentOrg.id,
        });
      }

      setShowRecorder(false);
      loadSummaries();

      if (selectedMeasurement) {
        loadMeasurementDetails(selectedMeasurement);
      }
    } catch (error) {
      console.error('Failed to record measurements:', error);
      throw error;
    }
  };

  const getStatusColor = (status?: 'normal' | 'warning' | 'critical') => {
    switch (status) {
      case 'critical':
        return 'text-red-600 bg-red-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-green-600 bg-green-100';
    }
  };

  const formatValue = (value: any, unit?: string) => {
    if (typeof value === 'number') {
      return `${value.toFixed(2)}${unit ? ` ${unit}` : ''}`;
    }
    return String(value);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading measurements...</div>
        </div>
      </div>
    );
  }

  if (showRecorder) {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Record Measurements</h3>
          <button
            onClick={() => setShowRecorder(false)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
        </div>
        <MeasurementRecorder
          definitions={measurementDefinitions}
          entityType={entityType}
          entityId={entityId}
          entityName={entityName}
          onRecord={handleRecordMeasurements}
          userId={currentUser?.uid || ''}
          userName={currentUser?.displayName || currentUser?.email || 'Unknown'}
        />
      </div>
    );
  }

  if (summaries.length === 0 && measurementDefinitions.length === 0) {
    return (
      <div className="card text-center py-12">
        <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No Measurements Configured
        </h3>
        <p className="text-gray-600 mb-4">
          Measurements can be defined in inspection templates
        </p>
      </div>
    );
  }

  if (summaries.length === 0) {
    return (
      <div className="card text-center py-12">
        <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Measurements Yet</h3>
        <p className="text-gray-600 mb-4">
          Start recording measurements to track performance over time
        </p>
        <button onClick={() => setShowRecorder(true)} className="btn btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Record First Measurement
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Current Measurements</h3>
          <button
            onClick={() => setShowRecorder(true)}
            className="btn btn-secondary btn-sm flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            Record
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {summaries.map((summary) => (
            <div
              key={summary.id}
              onClick={() => setSelectedMeasurement(summary.measurementDefId)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedMeasurement === summary.measurementDefId
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
              }`}
            >
              <div className="text-sm text-gray-600 mb-1">{summary.measurementName}</div>
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {formatValue(summary.latestValue, summary.unit)}
              </div>
              <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(summary.latestStatus)}`}>
                {summary.latestStatus}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chart Section */}
      {selectedMeasurement && trend && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Trend Analysis</h3>
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
              className="input w-32"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
            </select>
          </div>
          <MeasurementChart
            readings={[...measurementHistory].reverse()}
            trend={trend}
          />
        </div>
      )}

      {/* Recent Readings Table */}
      {selectedMeasurement && measurementHistory.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Readings</h3>
            <select
              value={readingsPeriodDays}
              onChange={(e) => setReadingsPeriodDays(Number(e.target.value))}
              className="input w-32"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Value
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Recorded By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {measurementHistory.map((reading) => (
                  <tr
                    key={reading.id}
                    onClick={() => reading.inspectionId && navigate(`/inspections/${reading.inspectionId}`)}
                    className={`${reading.inspectionId ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'}`}
                    title={reading.inspectionId ? 'Click to view inspection' : undefined}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        const date = reading.readingDate;
                        if (date instanceof Date) return date.toLocaleString();
                        if (typeof date === 'number') return new Date(date).toLocaleString();
                        if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
                          return date.toDate().toLocaleString();
                        }
                        if (date && typeof date === 'object' && 'seconds' in date && typeof date.seconds === 'number') {
                          return new Date(date.seconds * 1000).toLocaleString();
                        }
                        return String(date);
                      })()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatValue(reading.value, reading.unit)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getStatusColor(
                          reading.status
                        )}`}
                      >
                        {reading.status || 'normal'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-1">
                        <span className={reading.inspectionId ? 'text-blue-600 font-medium' : 'text-gray-600'}>
                          {reading.source}
                        </span>
                        {reading.inspectionId && (
                          <ArrowTopRightOnSquareIcon className="w-3 h-3 text-blue-600" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {reading.recordedByName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {reading.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
